import { MultiTypeAverageWindow } from '../utils/MultiTypeAverageWindow';
import {
  type EngagedRange,
  LayoutManager,
  type LayoutManagerOptions,
  type RVLayout,
  type RVLayoutInfo,
} from './LayoutManager';

const DEFAULT_ITEM_SIZE = 200;
const AVERAGE_WINDOW_CAPACITY = 10;

export interface MasonryLayoutManagerOptions extends LayoutManagerOptions {
  numColumns: number;
  /**
   * When true, single-column items are placed in the currently-shortest
   * column instead of round-robin; multi-column items pick the position
   * that minimizes total post-placement column heights. Tracks upstream's
   * `optimizeItemArrangement` prop. Default false.
   */
  optimizeItemArrangement?: boolean;
  /**
   * Mutator-style span override — same shape as GridLayoutManager. Mutate
   * `layout.span` (clamped to `numColumns`). Called once per item per
   * relayout pass.
   */
  overrideItemLayout?: (
    layout: { span?: number },
    index: number,
    maxColumns: number,
  ) => void;
}

interface MasonryLayoutCell extends RVLayout {
  /** Span in columns, after clamping to `numColumns`. */
  span: number;
}

/**
 * Pinterest-style layout: items stack into a fixed number of columns,
 * preserving natural heights per column instead of normalizing rows. Each
 * column's bottom advances as items are placed; placement strategy is
 * either round-robin or shortest-column (when `optimizeItemArrangement` is
 * enabled).
 *
 * Mirrors upstream FlashList v2's `RVMasonryLayoutManagerImpl`
 * (src/recyclerview/layout-managers/MasonryLayoutManager.ts). Vertical
 * only — masonry + horizontal isn't supported (matches upstream, which
 * throws `ErrorMessages.masonryAndHorizontalIncompatible`).
 */
export class MasonryLayoutManager extends LayoutManager {
  private layouts: MasonryLayoutCell[] = [];
  private readonly measuredSizes = new Map<number, number>();
  private readonly averageWindow: MultiTypeAverageWindow;
  private readonly defaultItemSize: number;
  private readonly numColumns: number;
  private readonly optimizeItemArrangement: boolean;
  private readonly overrideItemLayout?: (
    layout: { span?: number },
    index: number,
    maxColumns: number,
  ) => void;
  private readonly spanProbe: { span?: number } = {};

  constructor(options: MasonryLayoutManagerOptions) {
    super(options);
    if (this.horizontal) {
      throw new Error(
        'FlashList: masonry + horizontal is not supported. Pick one.',
      );
    }
    this.numColumns = Math.max(1, options.numColumns);
    this.defaultItemSize = options.defaultItemSize ?? DEFAULT_ITEM_SIZE;
    this.optimizeItemArrangement = options.optimizeItemArrangement ?? false;
    this.overrideItemLayout = options.overrideItemLayout;
    this.averageWindow = new MultiTypeAverageWindow(
      options.averageWindowCapacity ?? AVERAGE_WINDOW_CAPACITY,
      this.defaultItemSize,
    );
    this.recompute();
  }

  processLayoutInfo(infos: RVLayoutInfo[]): void {
    // Same defense as Linear/Grid — reject measurements before the scroller
    // has a viewport, and drop zero readings to avoid self-perpetuating
    // 0 → 0 feedback loops.
    if (this.windowSize.width === 0 || this.windowSize.height === 0) return;
    let changed = false;
    for (const { index, dimensions } of infos) {
      if (index < 0 || index >= this.count) continue;
      const reported = dimensions.height;
      if (reported === 0) continue;
      const previous = this.measuredSizes.get(index);
      if (previous === reported) continue;
      this.measuredSizes.set(index, reported);
      this.averageWindow.addValue(reported, this.typeOf(index));
      changed = true;
    }
    if (changed) this.recompute();
  }

  getLayout(index: number): RVLayout {
    const cell = this.layouts[index];
    if (cell) return cell;
    return {
      x: 0,
      y: 0,
      width: this.trackSize(),
      height: this.defaultItemSize,
      isMeasured: false,
    };
  }

  getItemHeight(index: number): number {
    return this.layouts[index]?.height ?? this.defaultItemSize;
  }

  getContentSize(): { width: number; height: number } {
    if (this.layouts.length === 0) {
      return { width: 0, height: 0 };
    }
    let tallest = 0;
    for (const cell of this.layouts) {
      const bottom = cell.y + cell.height;
      if (bottom > tallest) tallest = bottom;
    }
    return { width: this.windowSize.width, height: tallest };
  }

  findIndicesInRange(
    rangeStart: number,
    rangeEnd: number,
  ): EngagedRange | null {
    if (this.count === 0) return null;
    // Masonry doesn't have monotonic y across indices — item N+1 may have a
    // smaller y than item N when columns aren't synchronized. Linear scan
    // collects every index whose y-extent intersects the range, then
    // returns the inclusive min/max as a contiguous superset (overshoots
    // are safe; the recycler tolerates rendering some off-screen items).
    let start = -1;
    let end = -1;
    for (let index = 0; index < this.layouts.length; index++) {
      const cell = this.layouts[index];
      const cellStart = cell.y;
      const cellEnd = cell.y + cell.height;
      if (cellEnd > rangeStart && cellStart < rangeEnd) {
        if (start === -1 || index < start) start = index;
        if (index > end) end = index;
      }
    }
    if (start === -1) {
      return { start: this.count - 1, end: this.count - 1 };
    }
    return { start, end };
  }

  shiftMeasurements(offset: number): void {
    if (offset === 0) return;
    const entries = Array.from(this.measuredSizes.entries());
    this.measuredSizes.clear();
    for (const [oldIndex, size] of entries) {
      const newIndex = oldIndex + offset;
      if (newIndex >= 0 && newIndex < this.count) {
        this.measuredSizes.set(newIndex, size);
      }
    }
    this.recompute();
  }

  protected override onItemCountChanged(): void {
    for (const index of [...this.measuredSizes.keys()]) {
      if (index >= this.count) this.measuredSizes.delete(index);
    }
    this.recompute();
  }

  protected override onWindowSizeChanged(): void {
    this.recompute();
  }

  private trackSize(): number {
    return this.windowSize.width / this.numColumns;
  }

  private resolveSpan(index: number): number {
    if (!this.overrideItemLayout) return 1;
    this.spanProbe.span = undefined;
    this.overrideItemLayout(this.spanProbe, index, this.numColumns);
    return Math.min(Math.max(1, this.spanProbe.span ?? 1), this.numColumns);
  }

  private primarySizeFor(index: number): number {
    const measured = this.measuredSizes.get(index);
    if (measured != null) return measured;
    return this.averageWindow.getCurrentValue(this.typeOf(index));
  }

  private recompute(): void {
    const trackSize = this.trackSize();
    const cells: MasonryLayoutCell[] = new Array(this.count);
    const columnHeights = new Array<number>(this.numColumns).fill(0);
    let nextSequentialColumn = 0;

    for (let index = 0; index < this.count; index++) {
      const span = this.resolveSpan(index);
      const height = this.primarySizeFor(index);
      const width = trackSize * span;
      let startColumn: number;
      let y: number;

      if (this.optimizeItemArrangement) {
        if (span === 1) {
          startColumn = shortestColumn(columnHeights);
          y = columnHeights[startColumn];
        } else {
          ({ startColumn, y } = bestSpanPosition(columnHeights, span, height));
        }
      } else {
        if (nextSequentialColumn + span > this.numColumns) {
          nextSequentialColumn = 0;
        }
        startColumn = nextSequentialColumn;
        y = maxOver(columnHeights, startColumn, span);
      }

      cells[index] = {
        x: startColumn * trackSize,
        y,
        width,
        height,
        isMeasured: this.measuredSizes.has(index),
        span,
      };

      const newColumnHeight = y + height;
      for (let col = startColumn; col < startColumn + span; col++) {
        columnHeights[col] = newColumnHeight;
      }

      if (!this.optimizeItemArrangement) {
        nextSequentialColumn = (startColumn + span) % this.numColumns;
      }
    }

    this.layouts = cells;
  }
}

function maxOver(values: number[], startIndex: number, span: number): number {
  let max = values[startIndex];
  for (let i = startIndex + 1; i < startIndex + span; i++) {
    if (values[i] > max) max = values[i];
  }
  return max;
}

function shortestColumn(values: number[]): number {
  let bestIndex = 0;
  let bestValue = values[0];
  for (let i = 1; i < values.length; i++) {
    if (values[i] < bestValue) {
      bestValue = values[i];
      bestIndex = i;
    }
  }
  return bestIndex;
}

/**
 * For multi-column placement, find the start position that minimizes the
 * sum of all column heights after placing an item of the given height.
 * Mirrors upstream's `placeOptimizedMultiColumnItem`.
 */
function bestSpanPosition(
  columnHeights: number[],
  span: number,
  height: number,
): { startColumn: number; y: number } {
  let bestStart = 0;
  let bestTotal = Number.POSITIVE_INFINITY;
  let bestY = 0;
  for (let start = 0; start + span <= columnHeights.length; start++) {
    const candidateY = maxOver(columnHeights, start, span);
    let total = 0;
    for (let col = 0; col < columnHeights.length; col++) {
      total +=
        col >= start && col < start + span
          ? candidateY + height
          : columnHeights[col];
    }
    if (total < bestTotal) {
      bestTotal = total;
      bestStart = start;
      bestY = candidateY;
    }
  }
  return { startColumn: bestStart, y: bestY };
}
